import React, { useEffect, useCallback } from 'react';

import { Col, Form, Accordion, Card } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { setHours, setMinutes, subDays, setSeconds } from 'date-fns';

import GraphViewsDropdown from '@graphs/SingleStation/Dropdown/GraphViews';
import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';

import DatePicker from '@components/Common/AwpDatePicker';

import { INI_START_DATE, INI_END_DATE } from '@awp/constants';
import { resetForecast } from '@components/WeatherForecast/ForecastFilter';
import { setGraphViewId, getViewableEndDate, setViewableEndDate } from '@stores/graphViewPageSlice';

import { setEndDateEpoch, setAllFilteredWeatherStations } from '@stores/weatherStationSlice';

import {
  selectWeatherStation,
  selectEndDate,
  setEndDate,
  selectStartDate,
  setStartDate,
  selectDataRange,
} from '@stores/dataVisualizationSlice';
import AwpDataRange from '@components/Common/AwpDataRange';

import { WeatherStationDropdown } from '@components/Common/WeatherStationDropdown';
import { debounce } from '@awp/helpers/Utils';

const SetCurrentGraphView = (graphViewId) => async (dispatch) => {
  dispatch(setGraphViewId(graphViewId));
  resetForecast(dispatch);
};

const DataFilters = () => {
  const endDate = useSelector(selectEndDate);
  const startDate = useSelector(selectStartDate);
  const viewableEndDate = useSelector(getViewableEndDate);
  const dataRange = useSelector(selectDataRange);
  const weatherStation = useSelector(selectWeatherStation);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setEndDateEpoch(viewableEndDate.value));
    dispatch(setAllFilteredWeatherStations());
  }, [viewableEndDate]);

  //initialize the dataVisualization dates
  useEffect(() => {
    if (
      startDate.value === new Date(new Date(INI_START_DATE)).getTime() &&
      endDate.value === new Date(new Date(INI_END_DATE)).getTime()
    ) {
      const startDateValue = setSeconds(setMinutes(setHours(subDays(new Date(), dataRange), 0), 0), 0).getTime();
      const endDateValue = setSeconds(setMinutes(setHours(new Date(), 23), 59), 0).getTime();
      dispatch(
        setStartDate({
          value: startDateValue,
          error: '',
          disabled: false,
        }),
      );
      dispatch(
        setEndDate({
          value: endDateValue,
          error: '',
          disabled: false,
        }),
      );
    } else {
      dispatch(
        setViewableEndDate({
          value: endDate.value,
          error: '',
          disabled: false,
        }),
      );
    }
  }, []);

  const dispatchDateChanges = (newEndDate) => {
    //this function handles the change to the end date filter

    // updates the graphViewPage viewableEndDate value
    let todayDate = new Date();
    const endDateEpoch =
      new Date(newEndDate).toDateString() == todayDate.toDateString()
        ? todayDate.getTime()
        : setMinutes(setHours(new Date(newEndDate), 23), 59).getTime();

    dispatch(
      setViewableEndDate({
        value: endDateEpoch,
        error: '',
        disabled: false,
      }),
    );
    //set the dataVisualization start date
    dispatch(
      setStartDate({
        value:
          new Date(newEndDate).toDateString() == todayDate.toDateString()
            ? setSeconds(setMinutes(setHours(subDays(new Date(endDateEpoch), dataRange), 0), 0), 0).getTime()
            : setSeconds(setMinutes(setHours(subDays(new Date(endDateEpoch), dataRange - 1), 0), 0), 0).getTime(),
        error: '',
        disabled: false,
      }),
    );
    //set the dataVisualization end date
    dispatch(
      setEndDate({
        value: setSeconds(setMinutes(setHours(new Date(endDateEpoch), 23), 59), 0).getTime(),
        error: '',
        disabled: false,
      }),
    );

    resetForecast(dispatch);
  };

  // Debounce the dispatchDateChanges or any other logic in handleChange
  const handleChange = useCallback(
    debounce((val) => {
      dispatchDateChanges(val);
    }, 1000),
    [dispatchDateChanges],
  ); // Dependency array ensures stability if dispatchDateChanges changes

  return (
    <>
      <Accordion defaultActiveKey="0">
        <Card border="light" className="page-card">
          <CardHeaderToggle eventKey="0" as="h3" title={'Station & Graph Selection'} />
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <div>
                <Form>
                  <Form.Row>
                    <Col lg={4} md={6} sm={6} className="mb-4" style={{ zIndex: 20 }}>
                      <WeatherStationDropdown />
                    </Col>
                    <Col lg={3} md={6} sm={6} className="mb-3" style={{ zIndex: 16 }}>
                      <GraphViewsDropdown GraphViewChanged={SetCurrentGraphView} />
                    </Col>

                    <Col lg={2} md={6} sm={6} className="mb-2">
                      <div>
                        <Form.Label>
                          <b>End Date</b>
                        </Form.Label>
                        <div style={{ width: '100%' }}>
                          <DatePicker
                            elementState={viewableEndDate}
                            className="form-control input-sm"
                            dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                            handleChange={handleChange}
                            showTime={{ isEnabled: false }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col lg={3} md={6} sm={6} style={{ zIndex: 10 }}>
                      <AwpDataRange endDateValue={viewableEndDate.value} weatherStation={weatherStation} />
                    </Col>
                  </Form.Row>
                </Form>
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </>
  );
};

export default DataFilters;
